import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../services/api';
import { validatePassword } from '../utils/passwordValidator';
import '../styles/AuthPages.css';

const PasswordStrengthIndicator = ({ password }) => {
  const validation = validatePassword(password);
  
  if (!password) return null;

  const getStrengthColor = (score) => {
    if (score < 30) return '#dc3545'; // red
    if (score < 50) return '#fd7e14'; // orange
    if (score < 70) return '#ffc107'; // yellow
    if (score < 85) return '#20c997'; // teal
    return '#28a745'; // green
  };

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <div style={{
        height: '4px',
        backgroundColor: '#e9ecef',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div
          style={{
            height: '100%',
            width: `${validation.score}%`,
            backgroundColor: getStrengthColor(validation.score),
            transition: 'all 0.3s ease'
          }}
        />
      </div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '0.25rem',
        fontSize: '0.8rem'
      }}>
        <span style={{ color: getStrengthColor(validation.score), fontWeight: '500' }}>
          {validation.strength}
        </span>
        <span style={{ color: '#666' }}>
          {validation.score}/100
        </span>
      </div>
    </div>
  );
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validation, setValidation] = useState(null);
  const [token, setToken] = useState('');

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (!resetToken) {
      setError('Invalid or missing reset token. Please request a new password reset.');
      return;
    }
    setToken(resetToken);
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate password as user types
    if (name === 'newPassword') {
      setValidation(validatePassword(value));
    }

    // Clear messages when user starts typing
    if (message || error) {
      setMessage('');
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset token. Please request a new password reset.');
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.isValid) {
      setError('Password does not meet security requirements');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password reset successful! You can now login with your new password.');
        setFormData({ newPassword: '', confirmPassword: '' });
        
        // Redirect to login after success
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'An error occurred while resetting your password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token && !error) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">Set New Password</h1>
        </div>
        <p className="auth-subtitle">Enter your new password below</p>

        {message && <div className="auth-success">{message}</div>}
        {error && <div className="auth-error">{error}</div>}

        {!error.includes('Invalid') && (
          <form onSubmit={handleSubmit}>
            <div className="auth-form-group">
              <label className="auth-label">New Password</label>
              <div className="auth-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter your new password"
                  className="auth-input"
                  style={{ paddingRight: '2.5rem' }}
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="auth-link-btn"
                  style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <PasswordStrengthIndicator password={formData.newPassword} />
            </div>

            <div className="auth-form-group">
              <label className="auth-label">Confirm Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your new password"
                className="auth-input"
                disabled={loading}
              />
              {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Passwords do not match
                </div>
              )}
            </div>

            {validation && !validation.isValid && (
              <div className="auth-note" style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                  Password Requirements:
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                  <li>At least 8 characters long</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                  <li>One special character (!@#$%^&*()_+-=[]{}|;:,.&lt;&gt;?)</li>
                  <li>No spaces allowed</li>
                </ul>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !validation?.isValid || formData.newPassword !== formData.confirmPassword}
              className="auth-btn"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        )}

        <div className="auth-center">
          <Link to="/login" className="auth-link">← Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;